import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, getAdminToken, setAdminToken, clearAdminToken } from '../api/http.js';

const AdminAuthContext = createContext(null);

/**
 * Three states the provider can be in:
 *
 *   'restoring' — initial load, we found a token in storage and are
 *                 validating it via GET /api/admin/auth/me. Blocks
 *                 routing-aware components from rendering the wrong
 *                 surface for one frame. Cleared once the probe lands.
 *
 *   'authed'    — token is valid; `me` is populated. Routes that need
 *                 auth render normally.
 *
 *   'guest'     — no token, or token was rejected. Routes that need
 *                 auth bounce to /login.
 *
 * The 'noteshq.admin.unauth' window event (fired by http.js on any
 * 401) drops us back to 'guest' without manual hand-wiring at every
 * call site.
 *
 * @author <lukman@zshift.io>
 */
export function AdminAuthProvider({ children }) {
  const [status, setStatus] = useState(() => getAdminToken() ? 'restoring' : 'guest');
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);

  // Validate the cached token on mount. If /me succeeds we land in
  // 'authed'; any failure (expired, network, 401) drops to 'guest'.
  useEffect(() => {
    if (status !== 'restoring') return;
    let cancelled = false;
    api('/api/admin/auth/me')
      .then((u) => { if (!cancelled) { setMe(u); setStatus('authed'); } })
      .catch(() => { if (!cancelled) { clearAdminToken(); setStatus('guest'); } });
    return () => { cancelled = true; };
  }, [status]);

  // Global 401 listener — any api() call that 401s drops us to
  // 'guest', which triggers ProtectedRoute to bounce to /login.
  useEffect(() => {
    const onUnauth = () => {
      setMe(null);
      setStatus('guest');
    };
    window.addEventListener('noteshq.admin.unauth', onUnauth);
    return () => window.removeEventListener('noteshq.admin.unauth', onUnauth);
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const res = await api('/api/admin/auth/login', {
        method: 'POST',
        body:   { email, password },
      });
      if (!res?.token) throw new Error('Server did not return a token.');
      setAdminToken(res.token);
      setMe(res.admin);
      setStatus('authed');
      // Stash a copy of the AdminUserDto so a hard refresh has
      // something to paint immediately while /me revalidates.
      try { localStorage.setItem('noteshq.admin.me', JSON.stringify(res.admin || {})); } catch {}
      return res.admin;
    } catch (e) {
      setError(e.message || 'Sign-in failed.');
      throw e;
    }
  }, []);

  const logout = useCallback(() => {
    clearAdminToken();
    try { localStorage.removeItem('noteshq.admin.me'); } catch {}
    setMe(null);
    setStatus('guest');
    setError(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Swap the cached `me` object with a fresh DTO — e.g. after a
   * successful profile update from the Settings page. Updates the
   * localStorage mirror too so a hard refresh keeps showing the new
   * name without waiting for /me to revalidate.
   *
   * Doesn't re-fetch — callers pass the canonical DTO the server just
   * returned, which avoids an extra round-trip for the common case
   * (the PUT response IS the up-to-date row).
   */
  const updateMe = useCallback((next) => {
    if (!next) return;
    setMe(next);
    try { localStorage.setItem('noteshq.admin.me', JSON.stringify(next)); } catch {}
  }, []);

  return (
    <AdminAuthContext.Provider value={{ status, me, error, login, logout, clearError, updateMe }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return ctx;
}
